// Helpers for "try with another model" chips below image/video generations.
// Pure utilities — no store/socket side effects.

import {
  isAwaitingRequestedMedia,
  messageHasMediaOutput,
} from '@/constants/utils/message';

export const IMAGE_MODEL_TAG = 'image-generators';
export const VIDEO_MODEL_TAG = 'video-generators';
export const MAX_MODEL_SUGGESTIONS = 3;

function getMessageText(message) {
  if (!message) return '';
  return typeof message.content === 'string' ? message.content : String(message.content || '');
}

function findUserMessageForAssistant(assistantMessage, messages) {
  if (!assistantMessage || !Array.isArray(messages) || !messages.length) return null;

  if (assistantMessage.parentMessageId) {
    const parent = messages.find(
      (msg) => msg?.role === 'user'
        && (msg.id === assistantMessage.parentMessageId || msg._id === assistantMessage.parentMessageId)
    );
    if (parent) return parent;
  }

  const assistantIndex = messages.findIndex(
    (msg) => msg === assistantMessage
      || (assistantMessage.id && (msg.id === assistantMessage.id || msg._id === assistantMessage.id))
      || (assistantMessage._id && (msg.id === assistantMessage._id || msg._id === assistantMessage._id))
  );

  if (assistantIndex > 0) {
    for (let i = assistantIndex - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'user') return messages[i];
    }
  }

  return null;
}

export function getAssistantMediaKind(message) {
  if (!message || message.role !== 'assistant') return null;

  const requested = String(message.isRequestingMedia || '').toLowerCase();
  if (requested === 'image' || requested === 'video') return requested;

  if (messageHasMediaOutput(message, 'video')) return 'video';
  if (messageHasMediaOutput(message, 'image') || message.isImage) return 'image';

  return null;
}

function isMediaGenerationInFlight(message) {
  if (!message) return true;
  if (isAwaitingRequestedMedia(message)) return true;
  if (message.isStreaming || message.isTyping || message.isThinking) return true;
  return false;
}

function extractFileIds(userMessage) {
  if (!userMessage || !Array.isArray(userMessage.files)) return [];
  return userMessage.files
    .map((file) => (typeof file === 'object' ? file.id : file))
    .filter(Boolean);
}

function collectModelIds(message) {
  if (!message || !Array.isArray(message.modelIds)) return [];
  return message.modelIds.filter(Boolean).map(String);
}

/**
 * Resolve prompt + media kind + used models for an assistant media reply.
 * Returns null when the message isn't a completed image/video generation.
 */
export function resolveMediaGenerationContext(assistantMessage, messages) {
  if (assistantMessage?.role !== 'assistant') return null;

  const mediaKind = getAssistantMediaKind(assistantMessage);
  if (!mediaKind) return null;
  if (isMediaGenerationInFlight(assistantMessage)) return null;

  const userMessage = findUserMessageForAssistant(assistantMessage, messages);
  const prompt = getMessageText(userMessage)?.trim();
  if (!prompt) return null;

  return {
    prompt,
    mediaKind,
    usedModelIds: collectModelIds(assistantMessage),
    fileIds: extractFileIds(userMessage),
    action: mediaKind === 'video' ? 'create-video' : 'create-image',
    modelTag: mediaKind === 'video' ? VIDEO_MODEL_TAG : IMAGE_MODEL_TAG,
  };
}

export function resolveMediaGenerationContextForGroup(group, messages) {
  if (!group?.messages?.length) return null;

  const usedModelIds = [];
  let mediaKind = null;
  let anyInFlight = false;

  for (const msg of group.messages) {
    if (msg?.role !== 'assistant') continue;
    const kind = getAssistantMediaKind(msg);
    if (!kind) continue;
    if (!mediaKind) mediaKind = kind;
    if (isMediaGenerationInFlight(msg)) anyInFlight = true;
    usedModelIds.push(...collectModelIds(msg));
  }

  if (!mediaKind || anyInFlight) return null;

  let userMessage = null;
  if (group.parentMessageId) {
    userMessage = messages.find(
      (msg) => msg?.role === 'user'
        && (msg.id === group.parentMessageId || msg._id === group.parentMessageId)
    ) || null;
  }
  if (!userMessage) {
    userMessage = findUserMessageForAssistant(group.messages[0], messages);
  }

  const prompt = getMessageText(userMessage)?.trim();
  if (!prompt) return null;

  return {
    prompt,
    mediaKind,
    usedModelIds: [...new Set(usedModelIds)],
    fileIds: extractFileIds(userMessage),
    action: mediaKind === 'video' ? 'create-video' : 'create-image',
    modelTag: mediaKind === 'video' ? VIDEO_MODEL_TAG : IMAGE_MODEL_TAG,
  };
}

// Narrow post-processors that share image-generators / video-generators tags
// with general models. Mirrors backend model_capability_picker exclusions, plus
// slug/name catches for specialists that look like plain generators in metadata.
const EXCLUDED_MODEL_TAG_SLUGS = new Set(['upscaling']);
const SPECIALIZED_IMAGE_MODEL_RE = /(text-to-vector|vectorize|image2svg|\bsvg\b|upscale|remove-background|background-remov)/i;
// Avatar / lip-sync / talking-head pipelines aren't useful "try another model"
// alternatives for a normal text-to-video or image-to-video generation.
const SPECIALIZED_VIDEO_MODEL_RE = /(avatar|heygen|lipsync|lip-sync|lip_sync|talking-head|talking head|upscale)/i;
const IMAGE_TO_VIDEO_MODEL_RE = /(img-2-vid|img2vid|image-to-video|image to video)/i;

function getModelTagSlugs(model) {
  return (model?.tags || []).map((tag) => tag?.slug).filter(Boolean);
}

function modelRequiresImageInput(model) {
  return (model?.parameters || []).some(
    (param) => param?.validation?.required && param?.validation?.file?.fileType === 'image'
  );
}

function modelAcceptsImageInput(model) {
  return model?.capabilities?.input?.hasImageSupport === true || modelRequiresImageInput(model);
}

function getModelHaystack(model) {
  return `${model?.slug || ''} ${model?.name || ''}`;
}

function isSpecializedMediaModel(model, mediaKind = 'image') {
  if (!model) return true;
  if (getModelTagSlugs(model).some((slug) => EXCLUDED_MODEL_TAG_SLUGS.has(slug))) return true;

  const haystack = getModelHaystack(model);

  if (mediaKind === 'video') {
    // Unlike image pipelines, img2vid models may legitimately hide the text
    // box — don't blanket-exclude hidesTextInput for video.
    return SPECIALIZED_VIDEO_MODEL_RE.test(haystack);
  }

  if (model.capabilities?.input?.hidesTextInput === true) return true;
  return SPECIALIZED_IMAGE_MODEL_RE.test(haystack);
}

function isModelEligible(model, excludedIds, options = {}) {
  if (!model?.id) return false;
  if (excludedIds.has(String(model.id))) return false;
  if (model.flags?.isEnabled === false) return false;
  if (model.flags?.isDeprecated) return false;

  const mediaKind = options.mediaKind === 'video' ? 'video' : 'image';
  if (isSpecializedMediaModel(model, mediaKind)) return false;

  const hasSourceFiles = Boolean(options.hasSourceFiles);
  const haystack = getModelHaystack(model);

  if (mediaKind === 'video') {
    // Text→video: drop image-required / img2vid-only models.
    // Image→video: keep models that can actually consume the source image.
    if (!hasSourceFiles) {
      if (modelRequiresImageInput(model) || IMAGE_TO_VIDEO_MODEL_RE.test(haystack)) return false;
    } else if (!modelAcceptsImageInput(model)) {
      return false;
    }
    return true;
  }

  // Pure text→image retries shouldn't suggest file-required specialists.
  if (!hasSourceFiles && modelRequiresImageInput(model)) return false;

  return true;
}

/**
 * Pick alternative image/video models, excluding ones already used and
 * specialized pipelines (upscalers, vectorizers, file-only editors, etc.).
 * Prefers featured models, then higher orderWeight.
 */
export function getRecommendedMediaModels(models, options = {}) {
  const maxResults = options.maxResults ?? MAX_MODEL_SUGGESTIONS;
  const excludedIds = new Set((options.excludeModelIds || []).map(String));
  const catalog = Array.isArray(models) ? models : [];

  return catalog
    .filter((model) => isModelEligible(model, excludedIds, options))
    .sort((a, b) => {
      const featuredDelta = (b.flags?.isFeatured ? 1 : 0) - (a.flags?.isFeatured ? 1 : 0);
      if (featuredDelta) return featuredDelta;
      return (Number(b.orderWeight) || 0) - (Number(a.orderWeight) || 0);
    })
    .slice(0, maxResults);
}

export {
  isSpecializedMediaModel,
  modelRequiresImageInput,
};
